import { useEffect, useMemo } from "react";
import {
  type CpvCodeDto,
  useGetCpvCodesQuery,
} from "../../autogen/bff-api";
import { useApiError } from "../errors/useApiError";
import { buildTree, indexByCode, type CpvNode } from "./cpvHierarchy";

export type UseCpvCodesResult = {
  codes: CpvCodeDto[];
  tree: CpvNode[];
  codeIndex: Record<string, CpvCodeDto>;
  isLoading: boolean;
};

const EMPTY_CODES: CpvCodeDto[] = [];
const EMPTY_TREE: CpvNode[] = [];
const EMPTY_INDEX: Record<string, CpvCodeDto> = {};

export function useCpvCodes(): UseCpvCodesResult {
  const { data, isLoading, error } = useGetCpvCodesQuery();
  const { trigger } = useApiError();

  useEffect(() => {
    if (error) trigger(error);
  }, [error, trigger]);

  const codes = data?.codes ?? EMPTY_CODES;

  const tree = useMemo(() => {
    if (codes.length === 0) return EMPTY_TREE;
    return buildTree(codes);
  }, [codes]);

  const codeIndex = useMemo(() => {
    if (codes.length === 0) return EMPTY_INDEX;
    return indexByCode(codes);
  }, [codes]);

  return { codes, tree, codeIndex, isLoading };
}
